import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useMemo,
} from 'react';
import api from '../../api';
import { Accountant, User, ViewSelectProp } from '../../models/User';
import PopNotificationsContext from '../../providers/PopNotifications/PopNotifications.context';
import { useTranslation } from 'react-i18next';
import CurrentUserContext, { CallbackConfig } from './CurrentUser.context';
import utils, {
  checkIsUserSuperAdmin,
  checkIsUltraAdmin,
  getIsListingOwner,
  isEnterpriseRWC,
} from 'utils';
import { SUCCESS_MESSAGES } from 'request-responses';
import FourDotLoader from 'components/FourDotLoader';
import {
  Listing,
  STATUSES_TO_ENQUIRE,
  STATUSES_TO_WATCH,
} from 'models/Listing';
import NeedEmailVerificationModal from 'components/NeedEmailVerificationModal';
import useVerification from 'hooks/useVerification';
import EmailMissingModal from 'components/EmailMissingModal';
import ReminderToVerifyPhoneNumberModal from 'components/ReminderToVerifyPhoneNumberModal';
import useCallbackRef from 'hooks/useCallbackRef';
import { ModalRef } from 'ncoded-component-library/build/components/organisms/Modal/Modal.component';
import useFirst from 'hooks/useFirst';
import credentialsService from 'services/credentialsService';
import PhoneMissingModal from 'components/PhoneMissingModal';
import PhoneVerificationModal from 'components/PhoneVerificationModal';
import { useLocation } from 'react-router-dom';
import { OPEN_ENQUIRY_HASH } from 'data/global';

const PROFILE_PICTURE = 'profileImage';

export const multiSelectKeys: Array<keyof User> = [
  'preferredIncludeIndustries',
  'preferredExcludeIndustries',
  'preferredLocations',
];

type CurrentUserProps = {
  currentUser: User;
  setCurrentUser: Dispatch<SetStateAction<User>>;
};

const CurrentUser: React.FC<CurrentUserProps> = (props) => {
  const { children, currentUser, setCurrentUser } = props;

  const [
    reminderToVerifyPhoneNumberModal,
    reminderToVerifyPhoneNumberModalRef,
  ] = useCallbackRef<ModalRef>();

  const { pathname, hash } = useLocation();

  const { isFirst, onFirst } = useFirst();

  const isSuperAdmin = useMemo(
    () => checkIsUserSuperAdmin(currentUser?.role),
    [currentUser],
  );

  const isUltraAdmin = useMemo(
    () => checkIsUltraAdmin(currentUser?.role),
    [currentUser],
  );

  const {
    hasEnterprise,
    isEnterpriseHead,
    enterpriseMember,
    enterprise,
    isRWCMember,
  } = useMemo(() => {
    const enterpriseMember = currentUser?.enterpriseMember;

    const { isEnterpriseHead, enterprise } = enterpriseMember || {};

    return {
      enterpriseMember,
      isEnterpriseHead,
      enterprise,
      hasEnterprise: !!enterpriseMember,
      isRWCMember: isEnterpriseRWC(enterprise),
    };
  }, [currentUser?.enterpriseMember]);

  const { popServerError, popSuccess } = useContext(PopNotificationsContext);

  const {
    isUserVerified,
    enquireListingId,
    phoneVerificationModal,
    emailMissingModalRef,
    needEmailVerificationModalRef,
    phoneMissingModalRef,
    phoneVerificationModalRef,
    openVerificationModal,
    openPhoneVerificationModal,
  } = useVerification(currentUser);

  const { t } = useTranslation();

  const changePassword = useCallback(
    async (
      value: Record<string, string>,
      { onSuccess, onError }: CallbackConfig,
    ) => {
      const { oldPassword, newPassword } = value;
      try {
        await api.users.changePassword(oldPassword, newPassword);
        onSuccess?.();
        popSuccess(t(SUCCESS_MESSAGES.CHAGED_PASSWORD));
      } catch (e) {
        onError?.();
        popServerError(e);
      }
    },
    [popSuccess, popServerError, t],
  );

  const patchCurrentUser = useCallback(
    async (
      value: Partial<User>,
      { skipToast, onSuccess }: CallbackConfig = {
        skipToast: false,
        onSuccess: utils.noop,
      },
    ) => {
      if (!currentUser) return;

      const patchedValues = (
        Object.entries(value) as Array<[keyof User, User[keyof User]]>
      ).reduce((acc, [key, value]) => {
        let patchVal;
        if (multiSelectKeys.includes(key)) {
          patchVal = utils.multiSelectValueToStringArray(
            value as Array<Record<string, any> & { value: string }>,
          );
        } else {
          patchVal = value;
        }
        return { ...acc, [key]: patchVal };
      }, {} as Partial<User>);

      try {
        const {
          data: { user },
        } = await api.users.patchUser(currentUser._id, patchedValues);
        setCurrentUser(user);
        onSuccess?.();

        if (!skipToast) popSuccess(t(SUCCESS_MESSAGES.PACHED_USER));
      } catch (e) {
        popServerError(e);
      }
    },
    [currentUser, setCurrentUser, popSuccess, popServerError, t],
  );

  const uploadProfileImage = useCallback(
    async (value: File) => {
      if (!value) return;
      try {
        const profileImage = await api.users.uploadFile(
          currentUser?._id,
          value,
          PROFILE_PICTURE,
        );
        popSuccess(t(SUCCESS_MESSAGES.IMAGE_UPLOADED));
        setCurrentUser((oldUser) => ({ ...oldUser, profileImage }));
        return profileImage;
      } catch (e) {
        popServerError(e);
      }
    },
    [currentUser?._id, setCurrentUser, popSuccess, t, popServerError],
  );

  const deleteProfileImage = useCallback(async () => {
    try {
      const {
        data: { fileId },
      } = await api.users.deleteFile(
        currentUser?._id,
        PROFILE_PICTURE,
        currentUser.profileImage.id,
      );
      setCurrentUser((oldUser) => ({ ...oldUser, profileImage: null }));
      popSuccess(t(SUCCESS_MESSAGES.IMAGE_DELETED));
      return fileId;
    } catch (e) {
      popServerError(e);
    }
  }, [currentUser, setCurrentUser, popSuccess, popServerError, t]);

  const updateUnreadMessages = useCallback(() => {
    setCurrentUser((oldUser) => ({
      ...oldUser,
      totalUnreadMessages: oldUser.totalUnreadMessages - 1,
    }));
  }, [setCurrentUser]);

  const updateUnreadMessagesUnread = useCallback(() => {
    setCurrentUser((oldUser) => ({
      ...oldUser,
      totalUnreadMessages: oldUser.totalUnreadMessages + 1,
    }));
  }, [setCurrentUser]);

  const updateAccountants = useCallback(
    (accountant: Partial<Accountant>) => {
      setCurrentUser((oldUser) => ({
        ...oldUser,
        accountant,
      }));
    },
    [setCurrentUser],
  );

  const isListingEnquirableAndWatchableByMe = useCallback(
    (listing: Listing): [boolean, boolean] => {
      if (!listing) return [false, false];

      const { userId, isEnquired, stage, isCoOwner } = listing;

      const isListingOwner = getIsListingOwner(
        currentUser?._id,
        userId,
        isCoOwner,
      );

      const isEnquirable =
        !isSuperAdmin &&
        !isListingOwner &&
        !isEnquired &&
        STATUSES_TO_ENQUIRE.includes(stage);

      const isWatchlistable =
        !isListingOwner && STATUSES_TO_WATCH.includes(stage);

      return [!!isEnquirable, !!isWatchlistable];
    },
    [currentUser, isSuperAdmin],
  );

  const updateView = useCallback(
    (key: ViewSelectProp, value: boolean) => {
      try {
        api.users.patchUser(currentUser._id, { [key]: value });
        setCurrentUser((prev) => ({ ...prev, [key]: value }));
      } catch (e) {
        return;
      }
    },
    [currentUser, setCurrentUser],
  );

  const onUserUpdate = useCallback(
    (user: User) => setCurrentUser((prev) => ({ ...prev, ...user })),
    [setCurrentUser],
  );

  useEffect(() => {
    if (isFirst && reminderToVerifyPhoneNumberModal && currentUser) {
      onFirst(() => {
        const { hasRemindedToVerifyPhone, hasVerifiedContactNumber } =
          currentUser;

        if (!hasRemindedToVerifyPhone && !hasVerifiedContactNumber) {
          if (
            credentialsService.hasJustRegistered ||
            hash === OPEN_ENQUIRY_HASH
          ) {
            return;
          }

          reminderToVerifyPhoneNumberModal?.open();
        }
      });
    }
  }, [
    onFirst,
    currentUser,
    isFirst,
    pathname,
    reminderToVerifyPhoneNumberModal,
    hash,
  ]);

  return (
    <CurrentUserContext.Provider
      value={{
        currentUser,
        isUserVerified,
        isSuperAdmin,
        isUltraAdmin,
        hasEnterprise,
        isEnterpriseHead,
        isRWCMember,
        enterpriseMember,
        enterprise,
        openVerificationModal,
        openPhoneVerificationModal,
        changePassword,
        patchCurrentUser,
        uploadProfileImage,
        deleteProfileImage,
        updateUnreadMessages,
        updateUnreadMessagesUnread,
        setCurrentUser,
        isListingEnquirableAndWatchableByMe,
        updateAccountants,
        updateView,
      }}
    >
      {!currentUser ? <FourDotLoader /> : children}

      <ReminderToVerifyPhoneNumberModal
        ref={reminderToVerifyPhoneNumberModalRef}
        onVerifyClick={openPhoneVerificationModal}
      />
      <PhoneMissingModal
        ref={phoneMissingModalRef}
        onUserUpdate={onUserUpdate}
        onSuccess={phoneVerificationModal?.open}
      />
      <PhoneVerificationModal ref={phoneVerificationModalRef} />

      <EmailMissingModal
        ref={emailMissingModalRef}
        listingId={enquireListingId}
        onUserUpdate={onUserUpdate}
      />
      <NeedEmailVerificationModal
        ref={needEmailVerificationModalRef}
        listingId={enquireListingId}
      />
    </CurrentUserContext.Provider>
  );
};

export default CurrentUser;
